import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  AutoAwesome as MagicIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Lightbulb as SuggestionIcon,
  Close as CloseIcon,
  Refresh as RegenerateIcon,
} from '@mui/icons-material';
import { aiService, AIPromptRequest, AIResponse } from '../../services/aiService';
import { FormDefinition } from '../../types/forms';
import { Rule } from '../../types/rules';
import { PageDefinition } from '../../types/pages';
import { WorkflowDefinition } from '../../types/workflows';
import { gradients } from '../../utils/theme';

interface AIAssistantProps {
  onComponentsGenerated: (response: AIResponse) => void;
  disabled?: boolean;
  appContext?: {
    existingForms?: FormDefinition[];
    existingRules?: Rule[];
    existingPages?: PageDefinition[];
    existingWorkflows?: WorkflowDefinition[];
    appType?: string;
    appCategory?: string;
  };
}

const AIAssistant: React.FC<AIAssistantProps> = ({
  onComponentsGenerated,
  disabled = false,
  appContext
}) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastResponse, setLastResponse] = useState<AIResponse | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);

  const promptExamples = [
    "Create a loan application system with approval workflow",
    "Build a client management dashboard with forms and analytics",
    "Make an approval workflow with email notifications",
    "Create a simple contact form with validation rules",
    "Build a reporting dashboard with charts and metrics"
  ];

  const handleSubmit = async () => {
    if (!prompt.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const request: AIPromptRequest = {
        prompt: prompt.trim(),
        context: appContext
      };

      const response = await aiService.generateComponents(request);
      setLastResponse(response);
      
      if (response.success && response.components) {
        onComponentsGenerated(response);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      setLastResponse({
        success: false,
        error: 'Failed to generate components. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExampleClick = (example: string) => {
    setPrompt(example);
  };

  const handleRegenerate = () => {
    if (prompt) {
      handleSubmit();
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && event.ctrlKey) {
      handleSubmit();
    }
  };

  return (
    <Card sx={{ 
      mb: 3, 
      position: 'relative',
      overflow: 'visible',
      border: '2px solid transparent',
      borderRadius: 2,
      background: 'white',
      zIndex: 0,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: -2,
        left: -2,
        right: -2,
        bottom: -2,
        borderRadius: 2,
        background: `${gradients.secondary}`,
        zIndex: -1,
      }
    }}>
      <Accordion sx={{ border: 'none' }} expanded={isExpanded} onChange={() => setIsExpanded(!isExpanded)}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="ai-assistant-content"
          id="ai-assistant-header"
          sx={{
            background: gradients.secondary,
            color: 'white',
            '& .MuiAccordionSummary-expandIconWrapper': { color: 'white' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box>
              <Typography variant="h6" component="div">
                iBuild AI Platform
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Describe your app in natural language and I'll build it for you
              </Typography>
            </Box>
            {lastResponse?.success && (
              <Chip 
                icon={<CheckIcon />} 
                label="Components Generated" 
                color="secondary" 
                size="small" 
                sx={{ ml: 'auto', mr: 2 }}
              />
            )}
          </Box>
        </AccordionSummary>
        
        <AccordionDetails sx={{ p: 0 }}>
          <CardContent sx={{ pt: 2 }}>
            {/* Prompt Input */}
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                label="Describe your app..."
                placeholder="e.g., 'Create a loan application system with approval workflow and email notifications'"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={disabled || isLoading}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                      {lastResponse?.success && (
                        <Tooltip title="Regenerate with same prompt">
                          <IconButton
                            size="small"
                            onClick={handleRegenerate}
                            disabled={disabled || isLoading || !prompt.trim()}
                            color="primary"
                          >
                            <RegenerateIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )
                }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                  onClick={handleSubmit}
                  disabled={disabled || isLoading || !prompt.trim()}
                  size="large"
                >
                  {isLoading ? 'Generating...' : 'Generate App Components'}
                </Button>
                
                <Typography variant="body2" color="text.secondary">
                  Press Ctrl+Enter to generate
                </Typography>
              </Box>
            </Box>

            {/* Quick Examples */}
            {!lastResponse && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SuggestionIcon fontSize="small" />
                  Try these examples:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {promptExamples.map((example, index) => (
                    <Chip
                      key={index}
                      label={example}
                      variant="outlined"
                      onClick={() => handleExampleClick(example)}
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* AI Response */}
            {lastResponse && (
              <Box sx={{ mt: 3 }}>
                {lastResponse.success ? (
                  <Box>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        <strong>Components Generated Successfully!</strong>
                      </Typography>
                      {lastResponse.explanation && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {lastResponse.explanation}
                        </Typography>
                      )}
                    </Alert>

                    {/* Generated Components Summary */}
                    {lastResponse.components && (
                      <Card variant="outlined" sx={{ mb: 2 }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            Generated Components:
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {lastResponse.components.forms?.length && (
                              <Chip 
                                label={`${lastResponse.components.forms.length} Forms`} 
                                color="primary" 
                                size="small" 
                              />
                            )}
                            {lastResponse.components.rules?.length && (
                              <Chip 
                                label={`${lastResponse.components.rules.length} Rules`} 
                                color="secondary" 
                                size="small" 
                              />
                            )}
                            {lastResponse.components.pages?.length && (
                              <Chip 
                                label={`${lastResponse.components.pages.length} Pages`} 
                                color="info" 
                                size="small" 
                              />
                            )}
                            {lastResponse.components.workflows?.length && (
                              <Chip 
                                label={`${lastResponse.components.workflows.length} Workflows`} 
                                color="warning" 
                                size="small" 
                              />
                            )}
                            {lastResponse.components.hooks?.length && (
                              <Chip 
                                label={`${lastResponse.components.hooks.length} Hooks`} 
                                color="success" 
                                size="small" 
                              />
                            )}
                            {lastResponse.components.appMetadata && (
                              <Chip 
                                label="App Metadata" 
                                color="default" 
                                size="small" 
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    )}

                    {/* AI Suggestions */}
                    {lastResponse.suggestions && lastResponse.suggestions.length > 0 && (
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                            AI Suggestions:
                          </Typography>
                          <List dense>
                            {lastResponse.suggestions.map((suggestion, index) => (
                              <ListItem key={index} sx={{ pl: 0 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                  <SuggestionIcon fontSize="small" color="primary" />
                                </ListItemIcon>
                                <ListItemText primary={suggestion} />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                ) : (
                  <Alert 
                    severity="error" 
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => setLastResponse(null)}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                  >
                    <Typography variant="body2">
                      <strong>Generation Failed</strong>
                    </Typography>
                    <Typography variant="body2">
                      {lastResponse.error}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            <Divider sx={{ my: 2 }} />
            
            {/* Help Text */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              💡 <strong>Tip:</strong> Be specific about what you want. 
              Mention forms, workflows, dashboards, or integrations to get better results.
            </Typography>
          </CardContent>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

export default AIAssistant;
